import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Table } from "react-bootstrap";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  useReactTable,
} from "@tanstack/react-table";

import img_filter from "../../assets/filter.png";
import Filter from "./Filter";
import "./style.css";
import { Constants } from "@/shared/Constants";

const TanstackTable = ({
  data,
  columns,
  shouldBlinkRow,
  blinkColor,
  paging = true,
  showTopScrollBar = false,
}: {
  data: any[];
  columns: any[];
  shouldBlinkRow?: (row: any) => boolean;
  blinkColor?: (row: any) => string | undefined;
  paging?: boolean;
  showTopScrollBar?: boolean;
}) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const topInnerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const topScroll = topScrollRef.current;
    const topInner = topInnerRef.current;

    if (!container) return;

    const updateDimensions = () => {
      if (topInner) topInner.style.width = `${container.scrollWidth}px`;
    };

    updateDimensions();

    let isSyncing = false;

    const onTopScroll = () => {
      if (!topScroll || isSyncing) return;
      isSyncing = true;
      container.scrollLeft = topScroll.scrollLeft;
      isSyncing = false;
    };

    const onContainerScroll = () => {
      if (isSyncing) return;
      isSyncing = true;
      if (topScroll) topScroll.scrollLeft = container.scrollLeft;
      isSyncing = false;
    };

    container.addEventListener("scroll", onContainerScroll);
    if (topScroll) topScroll.addEventListener("scroll", onTopScroll);

    const ro = new ResizeObserver(updateDimensions);
    ro.observe(container);

    return () => {
      container.removeEventListener("scroll", onContainerScroll);
      if (topScroll) topScroll.removeEventListener("scroll", onTopScroll);
      ro.disconnect();
    };
  }, [showTopScrollBar]);

  const pageSizeList = useMemo(() => [20, 50, 100, 500, 1000], []);
  const [showFilter, setShowFilter] = useState(false);

  const table = useReactTable({
    data,
    columns,
    debugTable: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    //no need to pass pageCount or rowCount with client-side pagination as it is calculated automatically
    state: {
      pagination,
    },
  });

  const handleFilterClick = (e: any) => {
    setShowFilter(!showFilter);
  };

  return (
    <>
      {showTopScrollBar && (
        <div ref={topScrollRef} style={{ overflowX: "auto", overflowY: "hidden", height: "12px" }}>
          <div ref={topInnerRef} style={{ height: "1px" }} />
        </div>
      )}
      <div style={{ position: "relative" }}>
      <div ref={scrollContainerRef} style={{ overflowX: "auto" }}>
        <Table striped bordered hover size="sm" className="tanstack-table-main">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} colSpan={header.colSpan}>
                    <span
                      {...{
                        className: header.column.getCanSort() ? "cursor-pointer select-none" : "",
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: " 🔼",
                        desc: " 🔽",
                      }[header.column.getIsSorted() as string] ?? null}
                    </span>

                    <span>
                      <>
                        <img
                          alt="filter"
                          className="cursor-pointer header-filter-icon"
                          src={img_filter}
                          onClick={handleFilterClick}
                        />
                      </>
                      {header.column.getCanFilter() && showFilter ? (
                        <div>
                          <Filter column={header.column} table={table} />
                        </div>
                      ) : null}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              const isBlink = shouldBlinkRow && shouldBlinkRow(row.original);
              const rowBlinkColor = isBlink ? (blinkColor ? blinkColor(row.original) ?? Constants.FlashingColor_Red : Constants.FlashingColor_Red) : undefined;
              return (
                <tr
                  key={row.id}
                  className={isBlink ? "blinking-row" : ""}
                  style={isBlink ? ({ "--blink-color": rowBlinkColor } as React.CSSProperties) : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      </div>

      {paging && (
        <div className="d-flex items-center gap-2 mt-2">
          <Button className="border rounded p-1" onClick={() => table.firstPage()} disabled={!table.getCanPreviousPage()}>
            {"<<"}
          </Button>
          <Button className="border rounded p-1" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
            {"<"}
          </Button>
          <Button className="border rounded p-1" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
            {">"}
          </Button>
          <Button className="border rounded p-1" onClick={() => table.lastPage()} disabled={!table.getCanNextPage()}>
            {">>"}
          </Button>
          <span className="d-flex items-center gap-1 p-1">
            <div>Page</div>
            <strong>
              {table.getState().pagination.pageIndex + 1} of {table.getPageCount().toLocaleString()}
            </strong>
          </span>
          <span className="d-flex items-center gap-1">
            <span className="p-1"> | Go to page:</span>
            <input
              type="number"
              min="1"
              max={table.getPageCount()}
              defaultValue={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                table.setPageIndex(page);
              }}
              className="border p-1 rounded w-16"
            />
          </span>
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
          >
            {pageSizeList.map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
          <span className="d-flex gap-1 p-1">
            Showing {table.getRowModel().rows.length.toLocaleString()} of {table.getRowCount().toLocaleString()} Rows
          </span>
        </div>
      )}

      {/* <pre>{JSON.stringify(table.getState().pagination, null, 2)}</pre> */}
    </>
  );
};

export default TanstackTable;
